import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Printer, FileText
} from 'lucide-react';
import { useData } from '../contexts/DataContext';
import { dataFetch } from '../api';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import QRCode from 'qrcode';
import { reportValidationUrl } from '../utils/site';

const AuditCertificate = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getReport } = useData();
  const [report, setReport] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [qrCodeData, setQrCodeData] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const reportData = await getReport(id);
        const userId = localStorage.getItem('user_id');
        // ncb sorting uses 'sort' parameter.
        const res = await dataFetch(`read/audit_logs?report_id=${id}&user_id=${userId}&sort=created_at&order=asc`);

        if (reportData) {
          setReport(reportData);
          setLogs(res?.data || []);
          const qrUrl = await QRCode.toDataURL(reportValidationUrl(id), {
            width: 300,
            color: { dark: '#101B3D', light: '#FFFFFF' }
          });
          setQrCodeData(qrUrl);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-paper-alt">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-navy" role="status" aria-label="Carregando certificado"></div>
    </div>
  );

  // Sem guarda aqui o render abaixo acessa report.xxx com report=null e
  // quebra a árvore inteira (tela branca) — mesma classe de bug do ReportView.
  if (!report) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-paper-alt p-4 text-center">
      <div className="w-16 h-16 bg-paper-alt border border-line text-navy rounded-card flex items-center justify-center mb-6">
        <FileText className="w-8 h-8" aria-hidden="true" />
      </div>
      <h1 className="font-display text-xl font-bold text-navy mb-2">Certificado indisponível</h1>
      <p className="text-sm text-muted max-w-sm">
        Não foi possível carregar este certificado. O laudo pode não existir, ainda não ter sido registrado, ou você não tem acesso a ele.
      </p>
    </div>
  );

  const eventNames = {
    create: 'Criação do Documento',
    edit: 'Edição de Conteúdo',
    sign: 'Assinatura Digital Registrada',
    view: 'Visualização Registrada',
    revoke: 'Documento Revogado',
    version_up: 'Nova Versão Gerada'
  };

  return (
    <div className="min-h-screen bg-paper-alt pb-20 print:bg-white print:pb-0">
      <div className="bg-paper border-b border-line sticky top-0 z-50 no-print">
        <div className="max-w-5xl mx-auto px-4 h-16 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="flex items-center min-h-[44px] text-muted font-semibold hover:text-ink transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" aria-hidden="true" />Voltar ao laudo
          </button>
          <button onClick={() => window.print()} className="btn-primary text-xs uppercase tracking-widest">
            <Printer className="w-4 h-4" aria-hidden="true" /> Imprimir certificado
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 mt-8 print:mt-0 print:px-0">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-paper shadow-card p-10 sm:p-16 min-h-[1100px] print:shadow-none print:m-0 relative flex flex-col border border-line print:border-0 overflow-hidden">

          <div className="flex justify-between items-center mb-12 border-b-4 border-navy pb-8 relative z-10">
            <h1 className="font-display text-2xl font-bold text-navy">Certificado de autenticidade</h1>
            <div className="text-right">
              <p className="text-[10px] font-semibold text-muted uppercase tracking-widest">Emissão do certificado</p>
              <p className="text-sm font-semibold text-ink font-mono tabular-nums">{format(new Date(), 'dd/MM/yyyy HH:mm:ss')}</p>
            </div>
          </div>

          <div className="mb-12 relative z-10">
            <div className="bg-paper-alt border border-line rounded-card p-8 flex flex-col md:flex-row items-center gap-8">
              <div className="flex-1">
                <h2 className="mb-2 font-display text-lg font-bold text-ink">Carimbo de tempo do Laudando</h2>
                <p className="text-xs text-muted leading-relaxed mb-4">
                  O hash SHA-256 do conteúdo deste laudo foi registrado pelo Laudando no instante abaixo. Qualquer alteração no texto gera um hash diferente, e a verificação acusa a divergência.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-paper p-3 rounded-card border border-line">
                    <p className="text-[9px] font-semibold text-muted uppercase mb-1">Data e hora verificadas</p>
                    <p className="text-xs font-semibold text-ink font-mono">{report.timestamp_verified_at ? format(new Date(report.timestamp_verified_at), 'dd/MM/yyyy HH:mm:ss', { locale: ptBR }) : 'Pendente'}</p>
                  </div>
                  <div className="bg-paper p-3 rounded-card border border-line">
                    <p className="text-[9px] font-semibold text-muted uppercase mb-1">Selo de registro</p>
                    <p className="text-xs font-mono font-semibold text-ink break-all">{report.timestamp_seal || 'NÃO SELADO'}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-12 relative z-10">
            <div className="space-y-6">
              <div>
                <h3 className="text-[10px] font-semibold text-navy uppercase tracking-[0.2em] mb-3">
                  Dados do documento
                </h3>
                <div className="bg-paper-alt rounded-card p-6 border border-line space-y-4">
                  <div>
                    <p className="text-[9px] font-semibold text-muted uppercase">Identificador (UUID)</p>
                    <p className="text-xs font-mono font-semibold text-ink break-all">{report.id}</p>
                  </div>
                  <div>
                    <p className="text-[9px] font-semibold text-muted uppercase">Paciente</p>
                    <p className="text-sm font-bold text-ink">{report.patient_name}</p>
                  </div>
                  <div className="flex justify-between">
                    <div>
                      <p className="text-[9px] font-semibold text-muted uppercase">Versão</p>
                      <p className="text-sm font-bold text-ink tabular-nums">{report.version || 1}.0</p>
                    </div>
                    <div>
                      <p className="text-[9px] font-semibold text-muted uppercase">Status</p>
                      <p className="text-[10px] font-semibold uppercase text-selo">{report.status}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-[10px] font-semibold text-navy uppercase tracking-[0.2em] mb-3">
                  Integridade criptográfica
                </h3>
                <div className="bg-paper-alt rounded-card p-6 border border-line">
                  <p className="text-[9px] font-semibold text-muted uppercase mb-2">Hash SHA-256 do conteúdo original</p>
                  <code className="text-[10px] text-navy font-mono break-all leading-relaxed block bg-paper p-3 rounded-card border border-line">
                    {report.hash || 'NÃO ASSINADO'}
                  </code>
                </div>
              </div>
            </div>

            <div className="flex flex-col items-center justify-center p-8 border-2 border-dashed border-line rounded-card">
              <div className="w-40 h-40 mb-6">
                {qrCodeData && <img src={qrCodeData} alt="QR Code de validação pública" className="w-full h-full" />}
              </div>
              <p className="text-center text-[10px] font-semibold text-ink uppercase tracking-widest mb-2">Validação pública</p>
              <p className="text-center text-[10px] text-muted max-w-[200px] leading-relaxed">
                Aponte a câmera para ver o status atual deste documento.
              </p>
            </div>
          </div>

          <div className="mb-16 relative z-10">
            <h3 className="text-[10px] font-semibold text-navy uppercase tracking-[0.2em] mb-6">
              Histórico de auditoria
            </h3>
            <div className="space-y-4">
              {logs.map((log) => (
                <div key={log.id} className="relative pl-8 pb-4 border-l-2 border-line last:border-0 last:pb-0">
                  <div className="absolute left-[-9px] top-0 w-4 h-4 rounded-full bg-paper border-2 border-navy flex items-center justify-center">
                    <div className="w-1.5 h-1.5 rounded-full bg-navy"></div>
                  </div>
                  <div className="bg-paper-alt rounded-card p-5 border border-line flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                      <p className="text-[10px] font-semibold text-ink uppercase tracking-wide">{eventNames[log.event_type] || log.event_type}</p>
                      <p className="text-[10px] text-muted">{format(new Date(log.created_at), "dd 'de' MMMM 'de' yyyy 'às' HH:mm:ss", { locale: ptBR })}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[9px] font-semibold text-muted uppercase">IP de origem</p>
                      <p className="text-[10px] font-mono font-medium text-ink">{log.metadata?.client?.ip || 'Server'}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-auto pt-10 border-t border-line text-[9px] text-muted leading-relaxed text-justify relative z-10">
            <p className="mb-4 font-semibold text-ink uppercase">Sobre este registro</p>
            <p className="mb-4">
              Este certificado mostra que o laudo identificado acima foi registrado no Laudando na data e hora indicadas, com hash SHA-256 (NIST FIPS 180-4) do conteúdo. O carimbo de tempo é emitido pelo próprio Laudando, não por uma autoridade de carimbo de tempo credenciada. Ele comprova se o documento apresentado é idêntico ao registrado; não substitui assinatura digital com certificado ICP-Brasil.
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AuditCertificate;
